/**
 * pages/Agents.jsx  —  /agents ("Agents")
 *
 * Your own agents — fetched from DB (your userId). Templates and the
 * marketplace/community browsing live on their own page (/marketplace,
 * pages/CommunityPage.tsx); publish/unpublish now lives on the agent's own
 * details page (/agents/:agentId).
 *
 * Features:
 *  - RunDrawer for execution (shared component)
 *  - Create via the one-shot "Create agent" flow or the full Agent Builder
 *  - Heartbeat health indicators
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Bot, Search, Plus, Play,
  Loader2,
  Globe,
  Users, FolderKanban, Zap, MoreVertical, CalendarClock,
  PenLine, BarChart3, FlaskConical, User, Code2, Lightbulb,
  Mail, FileText, Database, Calendar, CheckSquare, Send, MessageCircle, Brain, Server, Package,
} from "lucide-react";
import { customAgentAPI, agentRunAPI, spacesAPI, gmailAPI, onboardingAPI } from "../../services/api";
import { useHeartbeat } from "../../hooks/useHeartbeat";
import { SKILL_LIBRARY } from "../agentWorkspace/workspaceData.ts";
import { AGENT_DOMAINS } from "../../config/agentDomains.ts";
import ScheduleRunModal from "./ScheduleRunModal.jsx";
import {
  EntityCardShell, EntityCardHeader, EntityCardDescription, EntityCardMeta,
  EntityCardMetrics, EntityCardFooter, EntityPrimaryButton, EntityIconButton,
} from "../ui/EntityCard.jsx";

const SKILL_BY_ID = new Map(SKILL_LIBRARY.map(s => [s.id, s]));
const SKILL_ICON_BY_NAME = {
  Search, Globe, FileText, Database, Calendar, FolderKanban,
  CheckSquare, Mail, Send, MessageCircle, Brain, Server, Package,
};
function skillTagIcon(name) {
  return (name && SKILL_ICON_BY_NAME[name]) || Package;
}

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  page:        "var(--lyra-color-surface)",
  surface:     "var(--lyra-color-surface)",
  surface2:    "var(--lyra-color-surface-2)",
  surface3:    "var(--app-surface-3)",
  border:      "var(--lyra-color-border)",
  border2:     "var(--app-border-strong)",
  ink:         "var(--lyra-color-text)",
  ink2:        "var(--app-text-2)",
  ink3:        "var(--lyra-color-text-muted)",
  ink4:        "var(--app-text-3)",
  accent:      "var(--lyra-color-primary)",
  accentLight: "var(--app-primary-soft)",
  accentMid:   "var(--app-primary-soft-2)",
  accentDark:  "var(--app-primary-deep)",
  green:       "var(--lyra-color-success)",
  greenBg:     "var(--lyra-color-success-bg)",
  greenBd:     "var(--app-success-border)",
  greenText:   "#14532D",
  violet:      "var(--lyra-color-primary)",
  violetBg:    "var(--app-primary-soft)",
  violetBd:    "var(--app-primary-soft-2)",
  violetText:  "var(--app-primary-deep)",
  red:         "var(--lyra-color-danger)",
  redBg:       "var(--lyra-color-danger-bg)",
  redBd:       "var(--app-danger-border)",
  amber:       "var(--lyra-color-warning)",
  amberBg:     "var(--lyra-color-warning-bg)",
  amberBd:     "var(--app-warning-border)",
};

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600&display=swap');
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes fadeIn  { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
  * { box-sizing: border-box; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 4px; }
  .agent-highlight { animation: agent-pulse 2s ease-out; }
  @keyframes agent-pulse {
    0%   { box-shadow: 0 0 0 0 rgba(127,119,221,0.45); }
    30%  { box-shadow: 0 0 0 10px rgba(127,119,221,0.12); }
    100% { box-shadow: 0 0 0 0 rgba(127,119,221,0); }
  }
`;

// (Graph cache moved into AgentGraph component file)

// Templates are fetched from the server. No hardcoded fallbacks are injected here —
// render an empty list when none are available so the UI reflects the server state.

// ── Atoms ─────────────────────────────────────────────────────────────────────


// Section header component removed (unused); keep markup inline where needed.

// Pick a restrained, professional line icon from the agent's purpose.
function agentGlyph(agent) {
  const text = `${agent.name || ""} ${agent.description || ""} ${agent.category || ""}`.toLowerCase();
  if (/research|\bsearch\b|\bweb\b|scrape|market|competitor/.test(text)) return Search;
  if (/writ|content|blog|\bcopy\b|\bedit\b|\bdraft\b|post/.test(text))   return PenLine;
  if (/\bdata\b|analyt|chart|\bcsv\b|databas|metric|report|insight/.test(text)) return BarChart3;
  if (/\bqa\b|test|quality|bug/.test(text))                              return FlaskConical;
  if (/personal|organi|assist|plan/.test(text))                         return User;
  if (/\bcode\b|develop|engineer|program|debug|script|deploy/.test(text)) return Code2;
  if (/product|roadmap|strategy/.test(text))                            return Lightbulb;
  return Bot;
}

// ── Member Card ("AI Team" view) ─────────────────────────────────────────────
// Whole card navigates to the agent's detail page (Run/Edit/Delete/Publish
// all live there already) — the mockup's card itself carries no action
// buttons, so the schedule option lives behind the existing corner menu
// instead of adding a new button to the resting card.
function MemberCard({ agent, runStats, spaceCount, scheduledRun, onSchedule }) {
  const navigate = useNavigate();
  const Glyph = agentGlyph(agent);
  const active = agent.enabled !== false;
  const [menuOpen, setMenuOpen] = useState(false);

  const tags = (agent.skills || []).map(s => {
    const id = typeof s === "string" ? s : s.skillId;
    const meta = SKILL_BY_ID.get(id);
    return meta ? { id, name: meta.name, icon: meta.icon } : null;
  }).filter(Boolean);

  const runsTotal = runStats?.total || 0;
  const successPct = runStats && runStats.total > 0 ? Math.round((runStats.success / runStats.total) * 100) : null;

  const menu = (
    <div style={{ position: "relative" }}>
      <button onClick={() => setMenuOpen(v => !v)} title="Agent actions" aria-label="Agent actions"
        style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, color: T.ink3, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
        <MoreVertical size={16} />
      </button>
      {menuOpen && (
        <>
          <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 10 }} />
          <div style={{ position: "absolute", right: 0, top: 38, zIndex: 11, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,.1)", minWidth: 170, overflow: "hidden" }}>
            <button onClick={() => { setMenuOpen(false); onSchedule && onSchedule(agent); }}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", border: "none", background: "none", textAlign: "left", cursor: "pointer", fontSize: 12.5, color: T.ink2, fontFamily: "inherit" }}>
              <CalendarClock size={13} /> {scheduledRun ? "Reschedule run" : "Schedule run"}
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <EntityCardShell
      onClick={() => navigate(`/agents/${agent._id || agent.id}`)}
    >
      <EntityCardHeader icon={Glyph} eyebrow="AI member" title={agent.name} status={scheduledRun ? "Scheduled" : (active ? "Active" : "Paused")} statusTone={active ? "success" : "accent"} menu={menu} />
      <EntityCardDescription>
        {agent.description || "No description"}
      </EntityCardDescription>
      <EntityCardMeta icon={tags[0] ? skillTagIcon(tags[0].icon) : Package}>
        {agent.category || "General"}{tags[0] ? ` · ${tags[0].name}` : " · General purpose"}
      </EntityCardMeta>
      <EntityCardMetrics items={[
        { value: runsTotal, label: "Runs" },
        { value: successPct == null ? "—" : `${successPct}%`, label: "Success", tone: successPct == null ? undefined : "success" },
        { value: spaceCount || 0, label: spaceCount === 1 ? "Space" : "Spaces" },
      ]} />
      <EntityCardFooter>
        <EntityPrimaryButton
          onClick={e => {
            e.stopPropagation();
            try {
              window.dispatchEvent(new CustomEvent('lyra:open-run-drawer', { detail: { agentId: agent._id || agent.id, spaceId: null } }));
            } catch (_) { /* no-op */ }
          }}
          title="Run agent"
        ><Play size={14} fill="currentColor" /> Run agent</EntityPrimaryButton>
        <EntityIconButton onClick={e => { e.stopPropagation(); navigate(`/agents/${agent._id || agent.id}`); }} title="Edit agent"><PenLine size={16} /></EntityIconButton>
      </EntityCardFooter>
    </EntityCardShell>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Agents() {
  const navigate = useNavigate();
  const location = useLocation();

  const userId = (() => {
    try {
      const token   = localStorage.getItem("authToken");
      if (!token) return null;
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.id || payload.userId || null;
    } catch { return null; }
  })();

  useHeartbeat(userId);

  // ── State ─────────────────────────────────────────────────────────────────
  const [agents,          setAgents]          = useState([]);
  const [gmailStatus,     setGmailStatus]     = useState(null);
  const [loading,         setLoading]         = useState(true);
  // wizard edit state removed; wizard UI not used here
  const [searchQ,         setSearchQ]         = useState("");
  const [isRunActive,     setIsRunActive]     = useState(false);
  const [allRuns, setAllRuns] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [memberView, setMemberView] = useState("all");
  const [scheduledByAgent, setScheduledByAgent] = useState({});
  const [schedulingAgent, setSchedulingAgent] = useState(null);
  // Domain filter — null means "All" (no filtering). Defaults to whatever
  // domains this user picked at onboarding, once agent data + onboarding
  // prefs have both loaded and at least one of those domains actually has
  // an agent (otherwise defaulting to a filter that hides everything would
  // be worse than just showing "All").
  const [selectedDomains, setSelectedDomains] = useState(null);
  const [domainDefaultApplied, setDomainDefaultApplied] = useState(false);
  // Domains this user has chosen in Settings/onboarding — kept separately
  // from `selectedDomains` (the filter) so the chip row can surface a domain
  // the user picked even before any agent exists in it yet (count "0").
  // Without this, checking a new box in Settings had no visible effect here
  // until an agent happened to carry that category, which read as "not synced".
  const [userChosenDomains, setUserChosenDomains] = useState([]);

  // A Settings "Domain Folders" link (?domain=product) always wins over the
  // onboarding-domains default below — it's an explicit navigation, not a
  // passive default.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const domainParam = params.get("domain");
    if (!domainParam) return;
    if (!AGENT_DOMAINS.some(d => d.id === domainParam)) return;
    setSelectedDomains(new Set([domainParam]));
    setDomainDefaultApplied(true);
  }, [location.search]);

  useEffect(() => {
    onboardingAPI.get().then(({ onboarding } = {}) => {
      const userDomains = onboarding?.domains || [];
      setUserChosenDomains(userDomains);
      if (domainDefaultApplied || userDomains.length === 0 || agents.length === 0) return;
      const present = new Set(agents.map(a => a.category || "general"));
      const intersect = userDomains.filter(d => present.has(d));
      if (intersect.length > 0) setSelectedDomains(new Set(intersect));
      setDomainDefaultApplied(true);
    }).catch(() => {});
  }, [agents, domainDefaultApplied]);

  // Re-read on focus so toggling a box on Settings and tabbing back here
  // shows up without a full reload.
  useEffect(() => {
    const onFocus = () => onboardingAPI.get().then(({ onboarding } = {}) => {
      setUserChosenDomains(onboarding?.domains || []);
    }).catch(() => {});
    window.addEventListener('lyra:domains-changed', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('lyra:domains-changed', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const loadScheduled = useCallback(() => {
    agentRunAPI.getScheduled().then(d => {
      const map = {};
      (d?.runs || []).forEach(r => { if (r.agentId) map[String(r.agentId)] = r; });
      setScheduledByAgent(map);
    }).catch(() => setScheduledByAgent({}));
  }, []);

  // ── Load data ─────────────────────────────────────────────────────────────
  const loadAgents = useCallback(async () => {
    try {
      const d = await customAgentAPI.list();
      setAgents(Array.isArray(d) ? d : d?.agents || []);
    } catch {
      // keep prior agents list on transient failure
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    loadAgents();
    gmailAPI.status().then(setGmailStatus).catch(() => setGmailStatus({ connected: false }));

    // Runs + spaces for the "AI Team" header stats and per-member Runs/Success/Spaces
    agentRunAPI.listAll({ limit: 500 }).then(d => setAllRuns(Array.isArray(d?.runs) ? d.runs : [])).catch(() => setAllRuns([]));
    spacesAPI.list({ filter: 'all', limit: 50 }).then(res => {
      const list = Array.isArray(res?.spaces) ? res.spaces : [];
      setSpaces(list.filter(s => s.isMember || s.isOwner));
    }).catch(() => setSpaces([]));
    loadScheduled();

    // Global run events emitted by RunDrawer
    const onStart = () => setIsRunActive(true);
    const onEnd   = () => setIsRunActive(false);
    window.addEventListener('lyra:run-start', onStart);
    window.addEventListener('lyra:run-end',   onEnd);
    return () => {
      window.removeEventListener('lyra:run-start', onStart);
      window.removeEventListener('lyra:run-end',   onEnd);
    };
  }, [loadAgents, loadScheduled]);

  useEffect(() => {
    window.addEventListener('lyra:agent-scheduled', loadScheduled);
    return () => window.removeEventListener('lyra:agent-scheduled', loadScheduled);
  }, [loadScheduled]);

  // Open RunDrawer when navigated with query ?openRun=1 or ?agentId=<id>
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const open = params.get('openRun');
    const agentId = params.get('agentId');
    if (open) {
      try { window.dispatchEvent(new CustomEvent('lyra:open-run-drawer', { detail: { agentId: null, spaceId: null } })); } catch (_) {}
      navigate(location.pathname, { replace: true });
    } else if (agentId) {
      try { window.dispatchEvent(new CustomEvent('lyra:open-run-drawer', { detail: { agentId, spaceId: null } })); } catch (_) {}
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, navigate, location.pathname]);

  // Highlight agent card referenced by URL hash (e.g. /agents#<id>)
  useEffect(() => {
    const hash = (location && location.hash) ? location.hash.replace(/^#/, '') : '';
    if (!hash) return;

    const tryFindAndHighlight = () => {
      const el = document.querySelector(`[data-agent-id="${hash}"]`);
      if (!el) return false;
      try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch(e) { el.scrollIntoView(); }
      el.classList.add('agent-highlight');
      setTimeout(() => el.classList.remove('agent-highlight'), 2000);
      return true;
    };

    tryFindAndHighlight();
  }, [location]);

  // handleWizardCreated removed (wizard component not rendered here).

  // ── Filtered lists ────────────────────────────────────────────────────────
  const filteredAgents    = agents.filter(a => a.name.toLowerCase().includes(searchQ.toLowerCase()));
  // Show Lyran agents first in the grid
  const sortedAgents = [...filteredAgents].sort((a, b) => (b.isLyran ? 1 : 0) - (a.isLyran ? 1 : 0));
  const statusFilteredAgents = sortedAgents.filter(a => {
    if (statusFilter === "active") return a.enabled !== false;
    if (statusFilter === "paused") return a.enabled === false;
    return true;
  }).filter(a => !selectedDomains || selectedDomains.has(a.category || "general"));

  // Domains that get a chip: either this user's agents actually have one
  // (real count), or the user picked it in Settings/onboarding (shown at
  // "0" until an agent lands there) — so ticking a box in Settings is
  // immediately visible here, not just once an agent happens to match it.
  const presentDomainCounts = agents.reduce((map, a) => {
    const id = a.category || "general";
    map[id] = (map[id] || 0) + 1;
    return map;
  }, {});
  const presentDomains = AGENT_DOMAINS.filter(d => presentDomainCounts[d.id] > 0 || userChosenDomains.includes(d.id));

  const toggleDomainFilter = (id) => {
    setSelectedDomains(prev => {
      const next = new Set(prev || presentDomains.map(d => d.id));
      if (next.has(id)) next.delete(id); else next.add(id);
      // Everything selected is functionally the same as "All" — collapse to null.
      return next.size >= presentDomains.length ? null : next;
    });
  };

  // ── "AI Team" header stats — real data from agentRunAPI / spacesAPI ────────
  const runStatsByAgent = useMemo(() => {
    const map = {};
    allRuns.forEach(r => {
      const id = String(r.agentId || "");
      if (!id) return;
      if (!map[id]) map[id] = { total: 0, success: 0 };
      map[id].total += 1;
      if (r.status === "success") map[id].success += 1;
    });
    return map;
  }, [allRuns]);

  const spacesByAgent = useMemo(() => {
    const map = {};
    spaces.forEach(sp => {
      (sp.members || []).forEach(m => {
        const aid = m.lyranAgentId ? String(m.lyranAgentId) : null;
        if (!aid) return;
        map[aid] = (map[aid] || 0) + 1;
      });
    });
    return map;
  }, [spaces]);

  const tasksThisWeek = useMemo(() => {
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return allRuns.filter(r => new Date(r.createdAt).getTime() >= weekAgo).length;
  }, [allRuns]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: T.page, fontFamily: "DM Sans, system-ui, sans-serif" }}>
      <style>{GLOBAL_CSS}</style>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "28px 32px 60px" }}>

      {/* Use global RunDrawer via event dispatch instead of mounting locally */}

      {schedulingAgent && (
        <ScheduleRunModal
          target={schedulingAgent}
          targetType="agent"
          existingRun={scheduledByAgent[String(schedulingAgent._id || schedulingAgent.id)]}
          onClose={() => setSchedulingAgent(null)}
          onScheduled={() => loadScheduled()}
          onCancelExisting={() => loadScheduled()}
        />
      )}

      {/* ── Run indicator — shown while RunDrawer is executing ── */}
      {isRunActive && (
        <div style={{ overflow: "hidden", height: 3, borderRadius: 99, marginBottom: 14, background: T.surface3, position: "relative" }}>
          <style>{`@keyframes runBar { 0% { left:-55%; } 100% { left:110%; } }`}</style>
          <div style={{ position: "absolute", top: 0, height: "100%", width: "55%", background: `linear-gradient(90deg, transparent, ${T.accent}, transparent)`, animation: "runBar 1.2s linear infinite", borderRadius: 99 }} />
        </div>
      )}

      {/* ── AI Team ── */}
      <div style={{ animation: "fadeIn .18s ease" }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, gap: 16, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 600, color: T.ink, marginBottom: 4 }}>Agents</div>
              <div style={{ fontSize: 13, color: T.ink3 }}>Your AI workforce. Collaborate, delegate and get work done together.</div>
            </div>
            <button onClick={() => navigate("/agents/create")}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 9, background: T.accent, color: "var(--app-on-accent)", border: "none", fontSize: 12.5, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 2px 8px undefined" }}>
              <Plus size={14} /> Create AI member
            </button>
          </div>

          {/* Stat tiles */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 20 }}>
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: "var(--app-primary-soft)", color: "var(--lyra-color-primary)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Users size={17} /></div>
              <div><div style={{ fontSize: 19, fontWeight: 700, color: T.ink }}>{agents.length}</div><div style={{ fontSize: 11, color: T.ink4 }}>AI members</div></div>
            </div>
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: "var(--lyra-color-warning-bg)", color: "#C2620C", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><FolderKanban size={17} /></div>
              <div><div style={{ fontSize: 19, fontWeight: 700, color: T.ink }}>{spaces.length}</div><div style={{ fontSize: 11, color: T.ink4 }}>Spaces</div></div>
            </div>
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: "var(--app-primary-soft)", color: "var(--lyra-color-primary)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Zap size={17} /></div>
              <div><div style={{ fontSize: 19, fontWeight: 700, color: T.ink }}>{tasksThisWeek}</div><div style={{ fontSize: 11, color: T.ink4 }}>Tasks this week</div></div>
            </div>
          </div>

          {/* Sub-tabs + search + status filter — the active indicator is its own
              line under the tab buttons, not the row's shared border, so it
              can't get visually swallowed by it. flex-end keeps the tabs
              bottom-aligned with the search/filter controls so that line sits
              exactly on the baseline. */}
          <div style={{ position: "relative", display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, marginBottom: 16, flexWrap: "wrap", paddingBottom: 10 }}>
            <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 1, background: T.border }} />
            <div style={{ display: "flex", gap: 18 }}>
              {[["all", "All members"], ["role", "By role"], ["space", "By space"]].map(([id, label]) => (
                <button key={id} onClick={() => setMemberView(id)}
                  style={{
                    position: "relative", border: "none", background: "none", padding: "4px 0 8px", fontSize: 13,
                    fontWeight: memberView === id ? 700 : 600, cursor: "pointer",
                    color: memberView === id ? T.accent : T.ink3,
                    borderBottom: memberView === id ? `2.5px solid ${T.accent}` : "2.5px solid transparent",
                  }}>
                  {label}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 9, padding: "7px 12px", width: 220 }}>
                <Search size={13} color={T.ink4} />
                <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search members…"
                  style={{ border: "none", background: "transparent", outline: "none", fontSize: 12.5, color: T.ink, width: "100%", fontFamily: "inherit" }} />
              </div>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                style={{ border: `1px solid ${T.border}`, borderRadius: 9, padding: "7px 10px", fontSize: 12.5, color: T.ink2, background: T.surface, fontFamily: "inherit", cursor: "pointer" }}>
                <option value="all">All status</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
            </div>
          </div>

          {/* Domain filter chips — a domain gets a chip once it either has an
              agent or the user picked it in Settings/onboarding (shown at
              "0" until an agent lands there). Defaults to this user's
              onboarding domains (see effect above); "All" clears the filter. */}
          {presentDomains.length > 1 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 16 }}>
              <button
                onClick={() => setSelectedDomains(null)}
                style={{
                  border: `1px solid ${!selectedDomains ? T.accent : T.border}`,
                  background: !selectedDomains ? T.accentLight : T.surface,
                  color: !selectedDomains ? T.accent : T.ink3,
                  borderRadius: 999, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                }}
              >
                All
              </button>
              {presentDomains.map(d => {
                const active = !selectedDomains || selectedDomains.has(d.id);
                return (
                  <button
                    key={d.id}
                    onClick={() => toggleDomainFilter(d.id)}
                    style={{
                      border: `1px solid ${active ? T.accent : T.border}`,
                      background: active ? T.accentLight : T.surface,
                      color: active ? T.accent : T.ink3,
                      borderRadius: 999, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                    }}
                  >
                    {d.label} <span style={{ opacity: 0.6 }}>({presentDomainCounts[d.id] || 0})</span>
                  </button>
                );
              })}
            </div>
          )}

          {loading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "60px 0", color: T.ink3 }}>
              <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /> Loading agents…
            </div>
          ) : statusFilteredAgents.length === 0 ? (
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: "40px 20px", textAlign: "center" }}>
              <Bot size={32} color={T.ink4} style={{ margin: "0 auto 12px" }} />
              <div style={{ fontSize: 14, fontWeight: 600, color: T.ink2, marginBottom: 6 }}>
                {searchQ ? "No members match your search" : "No AI members yet"}
              </div>
              <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 20 }}>Start from a template, describe it to AI, or build manually.</div>
              {!searchQ && (
                <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                  <button onClick={() => navigate("/agents/create")}
                    style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 20px", borderRadius: 9, background: T.surface2, color: T.ink2, border: `1px solid ${T.border}`, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}>
                    <Plus size={14} /> Create AI member
                  </button>
                </div>
              )}
            </div>
          ) : memberView === "all" ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
              {statusFilteredAgents.map(agent => {
                const id = String(agent._id || agent.id);
                return (
                  <MemberCard key={id} agent={agent} runStats={runStatsByAgent[id]} spaceCount={spacesByAgent[id]} scheduledRun={scheduledByAgent[id]} onSchedule={setSchedulingAgent} />
                );
              })}

              {/* Add AI member */}
              <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: "20px 16px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: T.surface2, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
                  <Plus size={20} color={T.ink3} />
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.ink, marginBottom: 3 }}>Add AI member</div>
                <div style={{ fontSize: 11.5, color: T.ink3, marginBottom: 16 }}>Create a new agent or invite from community.</div>
                <div style={{ display: "flex", gap: 8, width: "100%" }}>
                  <button onClick={() => navigate("/agents/create")}
                    style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "8px 0", borderRadius: 8, border: "none", background: T.accent, color: "var(--app-on-accent)", fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    <Plus size={12} /> Create new
                  </button>
                  <button onClick={() => navigate("/marketplace")}
                    style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, color: T.ink2, fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    Browse community
                  </button>
                </div>
              </div>
            </div>
          ) : memberView === "role" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
              {Array.from(
                statusFilteredAgents.reduce((map, agent) => {
                  const tag = agent.capabilities?.[0]
                    ? agent.capabilities[0].replace(/[_-]/g, " ").replace(/\b\w/g, c => c.toUpperCase())
                    : "General";
                  if (!map.has(tag)) map.set(tag, []);
                  map.get(tag).push(agent);
                  return map;
                }, new Map())
              ).map(([role, list]) => (
                <div key={role}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: T.ink2, marginBottom: 10 }}>{role} <span style={{ color: T.ink4, fontWeight: 500 }}>({list.length})</span></div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
                    {list.map(agent => {
                      const id = String(agent._id || agent.id);
                      return <MemberCard key={id} agent={agent} runStats={runStatsByAgent[id]} spaceCount={spacesByAgent[id]} scheduledRun={scheduledByAgent[id]} onSchedule={setSchedulingAgent} />;
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
              {spaces.map(sp => {
                const list = statusFilteredAgents.filter(a => (sp.members || []).some(m => String(m.lyranAgentId) === String(a._id || a.id)));
                if (list.length === 0) return null;
                return (
                  <div key={sp._id}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: T.ink2, marginBottom: 10 }}>{sp.name} <span style={{ color: T.ink4, fontWeight: 500 }}>({list.length})</span></div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
                      {list.map(agent => {
                        const id = String(agent._id || agent.id);
                        return <MemberCard key={id} agent={agent} runStats={runStatsByAgent[id]} spaceCount={spacesByAgent[id]} scheduledRun={scheduledByAgent[id]} onSchedule={setSchedulingAgent} />;
                      })}
                    </div>
                  </div>
                );
              })}
              {(() => {
                const unassigned = statusFilteredAgents.filter(a => !spaces.some(sp => (sp.members || []).some(m => String(m.lyranAgentId) === String(a._id || a.id))));
                if (unassigned.length === 0) return null;
                return (
                  <div>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: T.ink2, marginBottom: 10 }}>Not in a space <span style={{ color: T.ink4, fontWeight: 500 }}>({unassigned.length})</span></div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
                      {unassigned.map(agent => {
                        const id = String(agent._id || agent.id);
                        return <MemberCard key={id} agent={agent} runStats={runStatsByAgent[id]} spaceCount={spacesByAgent[id]} scheduledRun={scheduledByAgent[id]} onSchedule={setSchedulingAgent} />;
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}




